import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, ArrowRight, Brain, FlaskConical, TrendingUp, Trophy, FileWarning, GitCompareArrows, Copy, Check } from "lucide-react";
import { runMockOptimizer } from "../../lib/optimizer/run";
import { DEMO_SEED } from "../../lib/optimizer/seeds";
import { scoreAnswer } from "../../lib/optimizer/rubric";
import { stageWinner, listPending, approve, reject } from "../../lib/optimizer/pending";
import type { OptimizerResult, PromptVariant, OptimizerSeed, DesignerVoice, TargetPrompt } from "../../lib/optimizer/types";
import { PROMPT_CATALOG_YAML } from "../../data/generated/prompt-catalog.generated";
import { cls } from "../../lib/cls";

/* ──────────────────────────────────────────────────────────────────
   Prompt optimizer panel — admin-only.
   Shape adapted from revomind/optimizer (DSPy MIPROv2 + AdalFlow
   Text-Grad ensemble). The frontend simulates the ensemble locally
   using the same rubric the Python backend uses, so the side-by-side
   variant cards preview EXACTLY what the cron job would stage.
   ────────────────────────────────────────────────────────────────── */

const TARGETS: readonly { value: TargetPrompt; label: string }[] = [
  { value: "designer_copilot",     label: "Designer copilot" },
  { value: "client_copilot",       label: "Client copilot" },
  { value: "constraint_resolver",  label: "Constraint resolver" },
  { value: "substitute_suggester", label: "Substitute suggester" },
  { value: "activity_narrator",    label: "Activity narrator" },
];

const VOICES: readonly { value: DesignerVoice; label: string; tag: string }[] = [
  { value: "calm-expert",  label: "Calm expert",  tag: "ontology-led · cites the rule" },
  { value: "warm-coach",   label: "Warm coach",   tag: "homeowner-facing · friendly" },
  { value: "concise-pro",  label: "Concise pro",  tag: "acks · queued · done" },
];

export function OptimizerPanel(): JSX.Element {
  const [target, setTarget] = useState<TargetPrompt>("designer_copilot");
  const [voice, setVoice]   = useState<DesignerVoice>("calm-expert");
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState<OptimizerResult | null>(null);

  async function runOptimizer(): Promise<void> {
    setRunning(true);
    setResult(null);
    const seed: OptimizerSeed = {
      ...DEMO_SEED,
      target_prompt: target,
      designer_voice: voice,
      current_yaml: PROMPT_CATALOG_YAML,
    };
    try {
      const r = await runMockOptimizer(seed);
      setResult(r);
      if (r.winner) stageWinner(r);
    } finally {
      setRunning(false);
    }
  }

  return (
    <div className="panel p-4 mt-6">
      <header className="flex items-start justify-between gap-3 flex-wrap mb-3">
        <div>
          <div className="eyebrow inline-flex items-center gap-1.5">
            <FlaskConical className="w-3 h-3" /> Prompt optimizer · DSPy + AdalFlow
          </div>
          <h2 className="display-3 mt-0.5 text-ink-950">Optimise. Compare. Promote.</h2>
          <p className="text-[12.5px] text-ink-500 mt-1 max-w-2xl">
            Same ensemble we run nightly in revomind, adapted for interior-design prompts.
            DSPy MIPROv2 compiles instruction + few-shot demos; AdalFlow Text-Grad rewrites the
            body via NL gradients. Winner picked by lift on the deterministic rubric (ontology
            coverage · rule compliance · designer-voice · brevity).
          </p>
        </div>
        <button onClick={runOptimizer} disabled={running} className="btn btn-foyr !text-xs !py-2">
          <Sparkles className={cls("w-3.5 h-3.5", running && "animate-pulse")} />
          {running ? "Running ensemble…" : "Optimise prompt"}
        </button>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 mb-3">
        <div>
          <div className="text-[11px] uppercase tracking-wider text-ink-500 mb-1">Target prompt</div>
          <div className="flex items-center gap-1 flex-wrap">
            {TARGETS.map((t) => (
              <button
                key={t.value}
                onClick={() => setTarget(t.value)}
                className={cls(
                  "pill border text-[11px]",
                  target === t.value
                    ? "bg-ink-950 text-cream border-ink-950"
                    : "bg-cream-dark border-border text-ink-700 hover:border-foyr/40",
                )}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>
        <div>
          <div className="text-[11px] uppercase tracking-wider text-ink-500 mb-1">Designer voice</div>
          <div className="flex items-center gap-1 flex-wrap">
            {VOICES.map((v) => (
              <button
                key={v.value}
                onClick={() => setVoice(v.value)}
                className={cls(
                  "pill border text-[11px]",
                  voice === v.value
                    ? "bg-ink-950 text-cream border-ink-950"
                    : "bg-cream-dark border-border text-ink-700 hover:border-foyr/40",
                )}
                title={v.tag}
              >
                {v.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <PendingApprovals onChange={() => setResult((r) => r)} />

      <AnimatePresence mode="wait">
        {running ? (
          <RunningState key="run" />
        ) : result ? (
          <ResultView key={result.variants[0]?.variant_id ?? "r"} result={result} />
        ) : (
          <EmptyState key="empty" />
        )}
      </AnimatePresence>
    </div>
  );
}

function PendingApprovals({ onChange }: { onChange: () => void }): JSX.Element | null {
  const [pending, setPending] = useState(listPending());
  function refresh(): void { setPending(listPending()); onChange(); }
  if (pending.length === 0) return null;
  return (
    <div className="panel p-3 mb-3 border-l-2 border-foyr">
      <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
        <div className="flex items-center gap-1.5">
          <Sparkles className="w-3.5 h-3.5 text-foyr" />
          <span className="font-semibold text-ink-950 text-sm">Pending approvals · nightly cron staged {pending.length}</span>
        </div>
        <span className="pill bg-cream-dark border border-border text-ink-700 text-[10px]">human-in-the-loop</span>
      </div>
      <ul className="space-y-1.5">
        {pending.map((p) => (
          <li key={p.variant.variant_id} className="border border-border rounded-md p-2 bg-cream-dark/40">
            <div className="flex items-center justify-between flex-wrap gap-1.5">
              <div className="text-[12.5px] text-ink-950">
                <span className="font-semibold">{p.target}</span>{" "}
                <span className="text-ink-500">·</span>{" "}
                <span className="pill bg-ink-950 text-cream text-[10px] font-mono uppercase">{p.variant.backend}</span>{" "}
                <span className="text-ink-500 text-[11px]">
                  lift +{p.variant.lift_vs_current.toFixed(3)} · baseline {p.baseline_score.toFixed(3)} → {p.variant.avg_score.toFixed(3)}
                </span>
              </div>
              <div className="flex items-center gap-1.5">
                <button onClick={() => { reject(p.variant.variant_id); refresh(); }} className="btn btn-outline !text-xs !py-1">Reject</button>
                <button onClick={() => { approve(p.variant.variant_id); refresh(); }} className="btn btn-foyr !text-xs !py-1">Approve & promote</button>
              </div>
            </div>
            <div className="text-[10.5px] text-ink-500 mt-1 font-mono truncate" title={p.staged_path}>{p.staged_path}</div>
          </li>
        ))}
      </ul>
    </div>
  );
}

function EmptyState(): JSX.Element {
  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="border border-dashed border-border rounded-lg p-6 text-[12.5px] text-ink-500"
    >
      Press <strong className="text-ink-950">Optimise prompt</strong> to run the ensemble on the
      designer's current prompt against{" "}
      <span className="pill bg-cream-dark border border-border text-ink-700">{DEMO_SEED.failing.length} failing</span>{" "}
      and{" "}
      <span className="pill bg-cream-dark border border-border text-ink-700">{DEMO_SEED.golden.length} golden</span>{" "}
      samples. Variants are scored on the same rubric the nightly cron uses.
    </motion.div>
  );
}

function RunningState(): JSX.Element {
  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="grid grid-cols-1 sm:grid-cols-2 gap-3"
    >
      {(["dspy","adalflow"] as const).map((b) => (
        <div key={b} className="panel p-3 border-l-2 border-foyr animate-pulse">
          <div className="flex items-center gap-1.5 mb-2">
            <Brain className="w-3.5 h-3.5 text-foyr" />
            <span className="font-mono text-[11px] uppercase tracking-wider text-ink-700">{b}</span>
          </div>
          <div className="h-2 bg-cream-dark rounded w-3/4 mb-1.5" />
          <div className="h-2 bg-cream-dark rounded w-1/2" />
        </div>
      ))}
    </motion.div>
  );
}

function ResultView({ result }: { result: OptimizerResult }): JSX.Element {
  return (
    <motion.div initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }}>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-3">
        <KPI
          icon={<TrendingUp className="w-3.5 h-3.5" />}
          label="Baseline rubric"
          value={result.baseline_score.toFixed(3)}
          hint={`current ${result.seed.target_prompt} avg`}
        />
        <KPI
          icon={<GitCompareArrows className="w-3.5 h-3.5" />}
          label="Variants produced"
          value={String(result.variants.length)}
          hint={`${result.variants.map((v) => v.backend).join(" · ")}`}
        />
        <KPI
          icon={<Trophy className="w-3.5 h-3.5" />}
          label="Winner"
          value={result.winner ? result.winner.backend : "none"}
          hint={result.winner ? `lift +${result.winner.lift_vs_current.toFixed(3)}` : "below min_lift"}
          highlight={!!result.winner}
        />
      </div>

      {result.staged_path ? (
        <div className="text-[11px] text-fern bg-fern/10 border border-fern/30 rounded-md px-2.5 py-1.5 inline-flex items-center gap-1.5 mb-3">
          <Check className="w-3 h-3" /> Staged to{" "}
          <code className="font-mono text-ink-950">{result.staged_path}</code>
        </div>
      ) : (
        <div className="text-[11px] text-mulberry bg-mulberry/10 border border-mulberry/30 rounded-md px-2.5 py-1.5 inline-flex items-center gap-1.5 mb-3">
          <FileWarning className="w-3 h-3" /> No variant cleared the min_lift threshold — nothing staged.
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        {result.variants.map((v) => (
          <VariantCard
            key={v.variant_id}
            variant={v}
            seed={result.seed}
            isWinner={result.winner?.variant_id === v.variant_id}
          />
        ))}
      </div>
    </motion.div>
  );
}

function KPI({ icon, label, value, hint, highlight }: { icon: JSX.Element; label: string; value: string; hint?: string; highlight?: boolean }): JSX.Element {
  return (
    <div className={cls(
      "panel p-3",
      highlight && "border-foyr/40 bg-foyr/5",
    )}>
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-ink-500">
        {icon}
        {label}
      </div>
      <div className="text-2xl font-semibold text-ink-950 tabular mt-1">{value}</div>
      {hint ? <div className="text-[10px] text-ink-500 mt-0.5">{hint}</div> : null}
    </div>
  );
}

function VariantCard({ variant, seed, isWinner }: { variant: PromptVariant; seed: OptimizerSeed; isWinner: boolean }): JSX.Element {
  const [copied, setCopied] = useState(false);

  function copy(): void {
    navigator.clipboard.writeText(variant.proposed_yaml).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    });
  }

  const breakdowns = [...seed.failing, ...seed.golden].slice(0, 4).map((s) => ({
    sample: s,
    score: scoreAnswer({ answer: variant.proposed_yaml, sample: s, voice: seed.designer_voice }),
  }));

  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      className={cls(
        "panel p-3 space-y-2",
        isWinner ? "border-foyr/60 ring-1 ring-foyr/30 bg-cream" : "",
      )}
    >
      <div className="flex items-center justify-between flex-wrap gap-1.5">
        <div className="flex items-center gap-1.5">
          <span className="pill bg-ink-950 text-cream text-[10px] font-mono uppercase">{variant.backend}</span>
          {isWinner ? <span className="pill bg-foyr text-cream text-[10px] inline-flex items-center gap-1"><Trophy className="w-2.5 h-2.5" /> winner</span> : null}
          <span className="pill bg-cream-dark border border-border text-ink-700 text-[10px]">
            {variant.duration_seconds}s
          </span>
        </div>
        <button onClick={copy} className="btn btn-outline !text-[10px] !py-1">
          {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
          {copied ? "Copied" : "Copy YAML"}
        </button>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-1.5 text-[11px]">
        <Metric label="Score" value={variant.avg_score.toFixed(3)} tone="ink" />
        <Metric label="Lift" value={(variant.lift_vs_current >= 0 ? "+" : "") + variant.lift_vs_current.toFixed(3)} tone={variant.lift_vs_current >= 0 ? "fern" : "foyr"} />
        <Metric label="Body" value={`${variant.proposed_yaml.length} ch`} tone="ink" />
        <Metric label="Demos" value={variant.proposer_log.includes("demos=") ? "yes" : "no"} tone="ink" />
      </div>

      <div>
        <div className="text-[10px] uppercase tracking-wider text-ink-500 mb-1">Rubric on first samples</div>
        <table className="w-full text-[11px]">
          <thead>
            <tr className="text-ink-500">
              <th className="text-left font-normal">Sample</th>
              <th className="text-right font-normal">Ontology</th>
              <th className="text-right font-normal">Rules</th>
              <th className="text-right font-normal">Voice</th>
              <th className="text-right font-normal">Brevity</th>
              <th className="text-right font-normal">Total</th>
            </tr>
          </thead>
          <tbody>
            {breakdowns.map((b, i) => (
              <tr key={i} className="border-t border-border/60">
                <td className="py-1 pr-2 truncate text-ink-950 max-w-[200px]" title={b.sample.query}>{b.sample.query}</td>
                <td className="py-1 text-right tabular">{b.score.ontology_coverage.toFixed(2)}</td>
                <td className="py-1 text-right tabular">{b.score.rule_compliance.toFixed(2)}</td>
                <td className="py-1 text-right tabular">{b.score.voice_match.toFixed(2)}</td>
                <td className="py-1 text-right tabular">{b.score.brevity.toFixed(2)}</td>
                <td className="py-1 text-right tabular font-semibold text-ink-950">{b.score.total.toFixed(2)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <details className="text-[11px]">
        <summary className="cursor-pointer text-ink-500 hover:text-ink-950 inline-flex items-center gap-1">
          <ArrowRight className="w-3 h-3" /> Proposer log
        </summary>
        <pre className="mt-1 bg-cream-dark border border-border rounded p-2 text-[10.5px] whitespace-pre-wrap text-ink-700 max-h-[120px] overflow-auto font-mono">{variant.proposer_log}</pre>
      </details>
    </motion.div>
  );
}

function Metric({ label, value, tone }: { label: string; value: string; tone: "ink" | "fern" | "foyr" }): JSX.Element {
  const c = tone === "fern" ? "text-fern" : tone === "foyr" ? "text-foyr" : "text-ink-950";
  return (
    <div className="border border-border rounded-md p-1.5 bg-cream-dark/50">
      <div className="text-[9.5px] uppercase tracking-wider text-ink-500">{label}</div>
      <div className={cls("font-semibold tabular", c)}>{value}</div>
    </div>
  );
}
